/*
  t3p-filters.js (3P filter quality-of-life build, 28 Sep 2026)
  Collection pages only. Works alongside Enterprise's facet-filters.js, never replaces it:
  - Size split (height / width / thickness chips, class .t3p-dim) mapped onto the real
    "Door Size (mm)" checkboxes (.t3p-raw inside #facets), so the theme's own form
    serialisation and fetch keep doing the work.
  - Glazing buckets (.t3p-bucket) and duplicate-value groups (.t3p-group) tick their
    member checkboxes (.t3p-raw-input).
  - Applied size chips collapsed into one chip per chosen dimension.
  - ?size=h2040-w820-t35 bootstrap (menu links, PDP back-link, search suggestions).
  - Mobile: only Size open by default; sticky "Filter & sort" pill with live count;
    "Choose your door size" strip above the grid.
  - Result heading suffix and nearest-size suggestions in the empty state.
  - GA4 filter_apply event (Measurement Protocol, same route as the DoorVision snippet).
  Rollback: remove the script tag from snippets/facet-filters.liquid.
*/
(function () {
  'use strict';
  if (window.t3pFiltersLoaded) return;
  window.t3pFiltersLoaded = true;

  var KEY = 't3p_size';
  var state = { h: new Set(), w: new Set(), t: new Set() };
  var lastParams = null;

  function emptyState() { return { h: new Set(), w: new Set(), t: new Set() }; }
  function hasState(st) { return !!(st.h.size || st.w.size || st.t.size); }
  function setState(st) { state.h = new Set(st.h); state.w = new Set(st.w); state.t = new Set(st.t); }
  function sorted(set) { return Array.from(set).sort(function (a, b) { return a - b; }); }

  function saveMemory() {
    try {
      if (hasState(state)) {
        sessionStorage.setItem(KEY, JSON.stringify({ h: sorted(state.h), w: sorted(state.w), t: sorted(state.t) }));
      } else {
        sessionStorage.removeItem(KEY);
      }
    } catch (e) { /* storage blocked: memory is a convenience only */ }
  }

  function parseSizeParam(str) {
    var out = emptyState();
    if (!str) return out;
    String(str).split('-').forEach(function (part) {
      var k = part.charAt(0);
      if (k !== 'h' && k !== 'w' && k !== 't') return;
      part.slice(1).split(',').forEach(function (n) {
        var v = parseInt(n, 10);
        if (v > 0) out[k].add(v);
      });
    });
    return out;
  }

  function stateToParam(st) {
    var parts = [];
    ['h', 'w', 't'].forEach(function (k) {
      if (st[k].size) parts.push(k + sorted(st[k]).join(','));
    });
    return parts.join('-');
  }

  function describeState(st) {
    var H = sorted(st.h).join(' or ');
    var W = sorted(st.w).join(' or ');
    var T = sorted(st.t).join(' or ');
    if (H && W) return H + ' x ' + W + (T ? ' x ' + T : '') + ' mm';
    if (H) return H + ' mm high' + (T ? ', ' + T + ' mm thick' : '');
    if (W) return W + ' mm wide' + (T ? ', ' + T + ' mm thick' : '');
    if (T) return T + ' mm thick';
    return '';
  }

  function facetEl() { return document.querySelector('facet-filters'); }
  function rawItems() { return Array.prototype.slice.call(document.querySelectorAll('#facets .t3p-raw[data-t3p-h]')); }
  function dimsOf(li) { return { h: +li.dataset.t3pH, w: +li.dataset.t3pW, t: +li.dataset.t3pT }; }
  function matches(d, st) {
    return (!st.h.size || st.h.has(d.h)) && (!st.w.size || st.w.has(d.w)) && (!st.t.size || st.t.has(d.t));
  }
  // Shopify leaves values past its 100-value cap out of filter.active_values, so the
  // server never ticks the indexed size inputs. The address bar is the source of truth.
  function urlSizeValues() {
    var items = rawItems();
    if (!items.length) return [];
    var name = items[0].querySelector('input').name;
    return new URLSearchParams(location.search).getAll(name);
  }
  function checkRawFromUrl() {
    var want = new Set(urlSizeValues());
    rawItems().forEach(function (li) {
      var inp = li.querySelector('input');
      if (inp && want.has(inp.value)) inp.checked = true;
    });
  }

  function anyRawActive() {
    return rawItems().some(function (li) { var i = li.querySelector('input'); return i && i.checked; });
  }

  function syncRawFromState() {
    var any = false;
    var items = rawItems();
    items.forEach(function (li) {
      var inp = li.querySelector('input');
      if (!inp) return;
      inp.checked = hasState(state) ? matches(dimsOf(li), state) : false;
      if (inp.checked && !inp.disabled) any = true;
    });
    // A height/width pair the collection does not stock matches no real value. Submit a
    // placeholder so Shopify returns nothing and the empty state (nearest sizes) shows,
    // instead of the size filter silently dropping away.
    var form = document.getElementById('facets');
    var nm = form && form.querySelector('#t3p-nomatch');
    if (form && hasState(state) && !any && items.length) {
      if (!nm) {
        nm = document.createElement('input');
        nm.type = 'hidden';
        nm.id = 't3p-nomatch';
        nm.name = items[0].querySelector('input').name;
        nm.value = '0 x 0 x 0';
        form.appendChild(nm);
      }
    } else if (nm) {
      nm.remove();
    }
  }

  // Given the raw size checkboxes the server rendered as active, work out which
  // height / width / thickness choice produced them. Null when ambiguous.
  function inferState() {
    var items = rawItems();
    var usable = items.filter(function (li) { var i = li.querySelector('input'); return i && (!i.disabled || i.checked); });
    var active = usable.filter(function (li) { return li.querySelector('input').checked; }).map(dimsOf);
    if (!active.length) return null;
    var all = usable.map(dimsOf);
    var key = function (d) { return d.h + 'x' + d.w + 'x' + d.t; };
    var activeKeys = new Set(active.map(key));
    var H = new Set(active.map(function (d) { return d.h; }));
    var W = new Set(active.map(function (d) { return d.w; }));
    var T = new Set(active.map(function (d) { return d.t; }));
    var combos = [['h'], ['w'], ['t'], ['h', 'w'], ['h', 't'], ['w', 't'], ['h', 'w', 't']];
    for (var i = 0; i < combos.length; i += 1) {
      var st = emptyState();
      if (combos[i].indexOf('h') > -1) st.h = H;
      if (combos[i].indexOf('w') > -1) st.w = W;
      if (combos[i].indexOf('t') > -1) st.t = T;
      var m = all.filter(function (d) { return matches(d, st); }).map(key);
      if (m.length === activeKeys.size && m.every(function (k) { return activeKeys.has(k); })) return st;
    }
    return null;
  }

  function markDims() {
    document.querySelectorAll('.t3p-dim').forEach(function (inp) {
      inp.checked = state[inp.dataset.dim].has(+inp.value);
    });
    var summary = document.querySelector('[data-t3p-strip-summary]');
    if (summary) {
      var text = describeState(state);
      summary.hidden = !text;
      summary.innerHTML = text ? 'Showing ' + text + ' <button type="button" class="t3p-link t3p-clear-size">Clear size</button>' : '';
    }
  }

  function groupRaws(g) {
    var attr = g.dataset.t3pGroup !== undefined ? 't3pGroup' : 't3pBucket';
    var val = g.dataset[attr];
    var filter = g.closest('.filter') || document;
    return Array.prototype.filter.call(filter.querySelectorAll('.t3p-raw-input'), function (r) { return r.dataset[attr] === val; });
  }

  function markGroups() {
    document.querySelectorAll('#facets .t3p-group, #facets .t3p-bucket').forEach(function (g) {
      var raws = groupRaws(g);
      var enabled = raws.filter(function (r) { return !r.disabled || r.checked; });
      var on = enabled.filter(function (r) { return r.checked; });
      g.checked = enabled.length > 0 && on.length === enabled.length;
      g.indeterminate = on.length > 0 && on.length < enabled.length;
    });
  }

  function applyNow() {
    var f = facetEl();
    var filters = f && f.querySelector('.facets__filters');
    if (filters) filters.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function collapseSizeChips() {
    var list = document.querySelector('#facets .active-filters');
    if (!list) return;
    list.querySelectorAll('.t3p-dim-chip').forEach(function (c) { c.remove(); });
    var sizeChips = Array.prototype.slice.call(list.querySelectorAll('a.active-filter[data-t3p-size]'));
    if (hasState(state)) {
      var block = document.querySelector('#facets .facets__active-filters');
      if (block) block.hidden = false;
      sizeChips.forEach(function (a) { a.hidden = true; });
      var labels = { h: 'Height', w: 'Width', t: 'Thickness' };
      ['t', 'w', 'h'].forEach(function (k) {
        sorted(state[k]).reverse().forEach(function (v) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'active-filter t3p-dim-chip flex items-center text-current';
          b.dataset.dim = k;
          b.dataset.value = v;
          b.innerHTML = '<span class="t3p-x" aria-hidden="true">&times;</span>' + labels[k] + ' ' + v + ' mm';
          list.insertBefore(b, list.firstChild);
        });
      });
    } else {
      sizeChips.forEach(function (a) { a.hidden = false; });
    }
    // Glazing buckets: when every glass type in a bucket is active, one chip for the bucket.
    list.querySelectorAll('.t3p-bucket-chip, .t3p-group-chip').forEach(function (c) { c.remove(); });
    var byBucket = {};
    list.querySelectorAll('a.active-filter[data-t3p-bucket]').forEach(function (a) {
      a.hidden = false;
      (byBucket[a.dataset.t3pBucket] = byBucket[a.dataset.t3pBucket] || []).push(a);
    });
    Object.keys(byBucket).forEach(function (bucket) {
      var box = document.querySelector('#facets .t3p-bucket[data-t3p-bucket="' + bucket + '"]');
      if (!box || !box.checked || byBucket[bucket].length < 2) return;
      byBucket[bucket].forEach(function (a) { a.hidden = true; });
      var labelEl = box.closest('li').querySelector('.flex-auto');
      var label = labelEl ? labelEl.childNodes[0].textContent.trim() : bucket;
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'active-filter t3p-bucket-chip flex items-center text-current';
      b.dataset.bucket = bucket;
      b.innerHTML = '<span class="t3p-x" aria-hidden="true">&times;</span>' + label + ' glass';
      list.insertBefore(b, list.firstChild);
    });
    // Duplicate-value groups: one chip per group instead of one per spelling.
    var byGroup = {};
    list.querySelectorAll('a.active-filter[data-t3p-group]').forEach(function (a) {
      a.hidden = false;
      (byGroup[a.dataset.t3pGroup] = byGroup[a.dataset.t3pGroup] || []).push(a);
    });
    Object.keys(byGroup).forEach(function (g) {
      if (byGroup[g].length < 2) return;
      byGroup[g].forEach(function (a) { a.hidden = true; });
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'active-filter t3p-group-chip flex items-center text-current';
      b.dataset.group = g;
      b.innerHTML = '<span class="t3p-x" aria-hidden="true">&times;</span>' + byGroup[g][0].textContent.trim();
      list.insertBefore(b, byGroup[g][0]);
    });
    var header = document.querySelector('#facets .active-filters-header');
    if (header) {
      var n = list.querySelectorAll('a.active-filter:not([hidden]), .t3p-dim-chip, .t3p-bucket-chip, .t3p-group-chip').length;
      if (n > 0) header.textContent = n + (n === 1 ? ' Applied Filter' : ' Applied Filters');
    }
  }

  function updateHeading() {
    var suffix = document.querySelector('#filter-results .t3p-heading-size');
    if (suffix) suffix.textContent = hasState(state) ? ' in ' + describeState(state) : '';
    var countEl = document.querySelector('#filter-results [data-t3p-count]');
    var count = countEl ? countEl.dataset.t3pCount : null;
    if (count === null) {
      var btn = document.querySelector('.js-close-drawer-mob');
      var m = btn && btn.textContent.match(/\d[\d,]*/);
      if (m) count = m[0];
    }
    if (count !== null) {
      document.querySelectorAll('.t3p-count').forEach(function (el) { el.textContent = count; });
    }
  }

  function sizeData() {
    var el = document.getElementById('t3p-size-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function nearest() {
    var box = document.querySelector('#filter-results .t3p-nearest');
    if (!box) return;
    var data = sizeData();
    if (!data || !hasState(state)) { box.hidden = true; return; }
    var sizes = data.sizes.filter(function (s) { return s.n !== 0 && s.h > 0; }); // n = -1: past the 100 cap, count unknown
    var html = '';
    var alt = [];
    if (state.w.size) {
      var want = sorted(state.w)[0];
      var seen = {};
      sizes.filter(function (s) { return !state.h.size || state.h.has(s.h); })
        .forEach(function (s) { if (!state.w.has(s.w) && !seen[s.w]) { seen[s.w] = true; alt.push(s.w); } });
      alt.sort(function (a, b) { return Math.abs(a - want) - Math.abs(b - want); });
      alt = alt.slice(0, 4);
      if (alt.length) {
        html += '<p class="t3p-nearest__lead">Nearest widths we do' + (state.h.size ? ' at ' + sorted(state.h).join(' or ') + ' high' : '') + ':</p><ul class="t3p-chips" role="list">';
        alt.forEach(function (w) {
          html += '<li class="t3p-chip-item"><button type="button" class="t3p-chip t3p-chip--btn" data-t3p-swap="w" data-value="' + w + '">' + (state.h.size ? sorted(state.h)[0] + ' x ' : '') + w + '</button></li>';
        });
        html += '</ul>';
      }
    } else if (state.h.size) {
      var wantH = sorted(state.h)[0];
      var seenH = {};
      sizes.forEach(function (s) { if (!state.h.has(s.h) && !seenH[s.h]) { seenH[s.h] = true; alt.push(s.h); } });
      alt.sort(function (a, b) { return Math.abs(a - wantH) - Math.abs(b - wantH); });
      alt = alt.slice(0, 4);
      if (alt.length) {
        html += '<p class="t3p-nearest__lead">Nearest heights we do:</p><ul class="t3p-chips" role="list">';
        alt.forEach(function (h) {
          html += '<li class="t3p-chip-item"><button type="button" class="t3p-chip t3p-chip--btn" data-t3p-swap="h" data-value="' + h + '">' + h + '</button></li>';
        });
        html += '</ul>';
      }
    }
    html += '<p class="t3p-nearest__foot">Not sure which size? Call 1300 000 171 with the height and width of the door leaf, or <button type="button" class="t3p-link t3p-clear-size">clear the size</button> and browse.</p>';
    box.innerHTML = html;
    box.hidden = false;
  }

  function mobileCollapse() {
    var mq = (window.theme && theme.mediaQueries && theme.mediaQueries.md) || '(min-width: 769px)';
    if (window.matchMedia(mq).matches) return;
    document.querySelectorAll('#facets details.filter').forEach(function (d) {
      var kind = d.dataset.t3pKind;
      var active = d.querySelector('.js-clear-filter:not([hidden])');
      if (kind !== 'size' && !active) d.open = false;
    });
  }

  function setupSticky() {
    var pill = document.querySelector('.t3p-sticky');
    var toolbar = document.getElementById('products-toolbar');
    if (!pill || !toolbar || !('IntersectionObserver' in window)) return;
    var mq = (window.theme && theme.mediaQueries && theme.mediaQueries.lg) || '(min-width: 1024px)';
    var io = new IntersectionObserver(function (entries) {
      var e = entries[0];
      var passed = !e.isIntersecting && e.boundingClientRect.top < 0;
      pill.hidden = window.matchMedia(mq).matches || !passed;
    }, { threshold: 0 });
    io.observe(toolbar);
    pill.addEventListener('click', function () {
      var f = facetEl();
      if (f && typeof f.open === 'function') f.open();
    });
  }

  // GA4 Measurement Protocol, same stream and route as the DoorVision snippet.
  var MID = 'G-3VMEDX2GV8';
  var MP = 'https://www.google-analytics.com/mp/collect?measurement_id=' + MID + '&api_secret=QXxuaq1VQ6iknyei-SucMA';
  function cookie(name) {
    var m = document.cookie.match('(?:^|; )' + name.replace(/[.$?*|{}()\[\]\\\/\+^]/g, '\\$&') + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : '';
  }
  function gaIds() {
    var cid = null; var sid = null;
    var ga = cookie('_ga');
    if (ga) { var parts = ga.split('.'); if (parts.length >= 4) cid = parts[parts.length - 2] + '.' + parts[parts.length - 1]; }
    var gs = cookie('_ga_' + MID.replace('G-', ''));
    if (gs) { var sp = gs.split('.'); if (sp.length >= 3) sid = sp[2].replace(/^s/, '').split('$')[0]; }
    return { cid: cid, sid: sid };
  }
  function send(event, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, params));
    try {
      var ids = gaIds();
      if (!ids.cid) return;
      var p = Object.assign({ engagement_time_msec: 1, page_location: location.href, page_title: document.title }, params);
      if (ids.sid) p.session_id = ids.sid;
      fetch(MP, { method: 'POST', keepalive: true, body: JSON.stringify({ client_id: ids.cid, events: [{ name: event, params: p }] }) }).catch(function () {});
    } catch (e) { /* tracking never breaks the filter */ }
  }
  function currentFilterParams() {
    var out = [];
    new URLSearchParams(location.search).forEach(function (v, k) { if (k.indexOf('filter.') === 0 && v) out.push(k + '=' + v); });
    return out;
  }
  function trackFilterChange() {
    var now = currentFilterParams();
    if (lastParams === null) { lastParams = new Set(now); return; }
    var added = now.filter(function (p) { return !lastParams.has(p); });
    lastParams = new Set(now);
    if (!added.length) return;
    var facets = {};
    added.forEach(function (p) {
      var idx = p.indexOf('=');
      var k = p.slice(0, idx); var v = p.slice(idx + 1);
      var facet = k.replace('filter.v.option.', '').replace('filter.p.', '').replace('filter.v.', '').replace(/\s*\(mm\)/i, '').replace(/\+/g, ' ').trim();
      (facets[facet] = facets[facet] || []).push(v.replace(/\+/g, ' '));
    });
    var collection = (location.pathname.match(/\/collections\/([^/]+)/) || [])[1] || location.pathname;
    Object.keys(facets).forEach(function (facet) {
      var vals = facets[facet];
      var value = /size/i.test(facet) && hasState(state) ? describeState(state) : vals.slice(0, 5).join(' | ');
      send('filter_apply', { facet: facet, value: value.slice(0, 100), value_count: vals.length, collection: collection });
    });
  }

  function refresh() {
    markDims();
    markGroups();
    collapseSizeChips();
    updateHeading();
    nearest();
  }

  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!t || !t.matches) return;
    if (t.matches('.t3p-dim')) {
      var k = t.dataset.dim; var v = +t.value;
      if (t.checked) state[k].add(v); else state[k].delete(v);
      saveMemory();
      syncRawFromState();
      markDims();
      if (!t.closest('#facets')) applyNow();
      return;
    }
    if (t.matches('.t3p-group, .t3p-bucket')) {
      groupRaws(t).forEach(function (r) { if (!r.disabled) r.checked = t.checked; });
      t.indeterminate = false;
      return;
    }
    if (t.matches('.t3p-raw-input') && t.closest('[data-t3p-h]')) {
      var st = inferState();
      setState(st || emptyState());
      saveMemory();
      markDims();
    }
  }, true);

  document.addEventListener('click', function (e) {
    var chip = e.target.closest && e.target.closest('.t3p-dim-chip');
    if (chip) {
      e.preventDefault();
      state[chip.dataset.dim].delete(+chip.dataset.value);
      saveMemory(); syncRawFromState(); markDims(); applyNow();
      return;
    }
    var bchip = e.target.closest && e.target.closest('.t3p-bucket-chip');
    if (bchip) {
      e.preventDefault();
      document.querySelectorAll('#facets .t3p-raw-input[data-t3p-bucket="' + bchip.dataset.bucket + '"]').forEach(function (r) { r.checked = false; });
      var bbox = document.querySelector('#facets .t3p-bucket[data-t3p-bucket="' + bchip.dataset.bucket + '"]');
      if (bbox) { bbox.checked = false; bbox.indeterminate = false; }
      applyNow();
      return;
    }
    var gchip = e.target.closest && e.target.closest('.t3p-group-chip');
    if (gchip) {
      e.preventDefault();
      document.querySelectorAll('#facets .t3p-raw-input[data-t3p-group="' + gchip.dataset.group + '"]').forEach(function (r) { r.checked = false; });
      var gbox = document.querySelector('#facets .t3p-group[data-t3p-group="' + gchip.dataset.group + '"]');
      if (gbox) { gbox.checked = false; gbox.indeterminate = false; }
      applyNow();
      return;
    }
    var clear = e.target.closest && e.target.closest('.t3p-clear-size');
    if (clear) {
      e.preventDefault();
      setState(emptyState()); saveMemory(); syncRawFromState(); markDims(); applyNow();
      return;
    }
    var swap = e.target.closest && e.target.closest('[data-t3p-swap]');
    if (swap) {
      e.preventDefault();
      var dim = swap.dataset.t3pSwap;
      state[dim] = new Set([+swap.dataset.value]);
      saveMemory(); syncRawFromState(); markDims(); applyNow();
    }
  });

  function init() {
    if (window.t3pFiltersInit) return;
    window.t3pFiltersInit = true;
    var params = new URLSearchParams(location.search);
    var sizeParam = params.get('size');
    if (sizeParam && rawItems().length) {
      var wanted = parseSizeParam(sizeParam);
      // Drop dimensions this collection does not offer, so a stray value never empties the grid.
      var offered = { h: new Set(), w: new Set(), t: new Set() };
      rawItems().forEach(function (li) { var d = dimsOf(li); offered.h.add(d.h); offered.w.add(d.w); offered.t.add(d.t); });
      ['h', 'w', 't'].forEach(function (k) { wanted[k] = new Set(Array.from(wanted[k]).filter(function (v) { return offered[k].has(v); })); });
      setState(wanted);
      saveMemory();
      syncRawFromState();
      refresh();
      lastParams = new Set(currentFilterParams());
      if (hasState(state)) applyNow();
    } else {
      checkRawFromUrl();
      var inferred = anyRawActive() ? inferState() : null;
      setState(inferred || emptyState());
      refresh();
      lastParams = new Set(currentFilterParams());
    }
    mobileCollapse();
    setupSticky();
    document.addEventListener('on:facet-filters:updated', function () {
      if (!urlSizeValues().length) {
        // size cleared (Clear all, chip, back button): drop the remembered choice
        setState(emptyState());
        saveMemory();
      } else if (hasState(state)) {
        syncRawFromState();
      } else {
        checkRawFromUrl();
        setState(inferState() || emptyState());
        saveMemory();
      }
      refresh();
      trackFilterChange();
    });
  }

  function whenReady() {
    if (!document.getElementById('facets')) return;
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('facet-filters').then(init);
    } else {
      init();
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', whenReady); else whenReady();
})();
